import { type FormStepperProps, useFormStepper } from '@1trading/react/components/ui/form-stepper';
import { type FieldValues, type UseFormReturn, useFormContext } from 'react-hook-form';
import { type To, useNavigate } from 'react-router-dom';

import type { FormStepComponent, FormStepDirection } from '@/types/components';

type FormStepGuardNavigateOptions = {
  to: To;
  replace?: boolean;
};

type FormStepGuardContext<T extends FieldValues = FieldValues> = {
  direction: FormStepDirection;
  form: UseFormReturn<T>;
  formStepper: ReturnType<typeof useFormStepper>;
  onSubmit: FormStepperProps<T>['onSubmit'];
  steps: FormStepComponent<T>[];
};

type FormStepGuardNext = {
  (): void;
  (_: false): void;
  (step: number): void;
  (navigateOptions: FormStepGuardNavigateOptions): void;
};

export type FormStepGuard<T extends FieldValues = FieldValues> = (context: FormStepGuardContext<T>, next: FormStepGuardNext) => Promise<void>;

type UseFormStepGuardProps<T extends FieldValues = FieldValues> = {
  onSubmit: (data: T) => void;
  steps: FormStepComponent<T>[];
};

export const useFormStepGuard = <T extends FieldValues = FieldValues>({ onSubmit, steps }: UseFormStepGuardProps<T>) => {
  const form = useFormContext<T>();
  const formStepper = useFormStepper();
  const navigate = useNavigate();
  const { setStep } = formStepper;

  const executeGuard = async (guard: FormStepGuard<T>, direction: FormStepDirection) => {
    return new Promise<boolean>((resolve) => {
      const next: FormStepGuardNext = async (result?: false | number | FormStepGuardNavigateOptions) => {
        if (direction === 'next') {
          if (!(await form.trigger())) {
            resolve(false);
            return;
          }
        }

        if (result === false) {
          resolve(false);
        } else if (typeof result === 'number') {
          setStep(result);
          resolve(false);
        } else if (typeof result === 'object') {
          navigate(result.to, { replace: result.replace });
          resolve(false);
        } else {
          resolve(true);
        }
      };

      guard({ direction, form, formStepper, onSubmit, steps }, next);
    });
  };

  return { executeGuard };
};
